/**
 * Voice-activity detection for the two hands-free chat modes.
 *
 * A hand-rolled RMS energy meter over Web Audio — deliberately NOT a neural
 * VAD. Silero/onnxruntime needs WASM glue that trips Content-Security-Policy,
 * while AudioContext + getUserMedia + MediaRecorder need none of that and work
 * in every browser that can record (Brave included).
 *
 * One mic stream, one meter, one recorder — both voice modes share it and
 * differ only in turn-ending policy (see hooks/useVoiceSession.js):
 *
 *   conversation — a clip IS a turn; a long silence ends it.
 *   walkie       — a clip is one segment of your turn; only the send word
 *                  (or the Send button) ends the turn.
 *
 * The caller gates the meter with setState(). While 'idle' no clip is opened
 * or kept, which is what makes the loop half-duplex: it can never transcribe
 * our own spoken reply. 'bargein' is the opt-in exception — see below.
 */

const POLL_MS = 50; // sample energy 20x/sec

/**
 * Quiet room ≈ 0.002 RMS, speech ≈ 0.05+, so 0.018 splits them — in a QUIET
 * room. Anywhere noisy (a car, a cafe) the ambient floor sits at or above
 * 0.018, "silence" never registers, and every clip runs to the utterance cap.
 * The floor is therefore ADAPTIVE: a slow EMA of sub-threshold energy, with
 * the speech threshold riding FLOOR_MULT above it and clamped so a loud room
 * still can't push speech out of reach.
 */
const BASE_RMS = 0.018;
const FLOOR_MULT = 3;
const MAX_RMS = 0.06;
/** EMA weight per poll — converges on a new ambient level in roughly 2s. */
const FLOOR_ALPHA = 0.02;

/** Clips shorter than this (or tiny in bytes) are coughs and knocks — drop. */
const MIN_UTTERANCE_MS = 350;
const MIN_CLIP_BYTES = 1200;

/**
 * Barge-in: while the app is speaking, the mic also hears the app (measured
 * on Android Chrome — the speaker-to-mic path is real even with the browser's
 * echo cancellation on). So interrupting by voice demands a much louder,
 * sustained signal than normal turn-taking, and even then it is opt-in.
 */
const BARGE_MULT = 2.5;
const BARGE_SUSTAIN_MS = 450;

const MIMES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4', // Safari/iOS
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

function pickMime() {
  if (typeof MediaRecorder === 'undefined') return '';
  return MIMES.find((m) => MediaRecorder.isTypeSupported(m)) || '';
}

/** Can a real mic + recorder exist here? (getUserMedia needs a secure context.) */
export function canUseVoice() {
  return Boolean(
    typeof window !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== 'undefined' &&
      (window.AudioContext || window.webkitAudioContext) &&
      (window.isSecureContext ?? true)
  );
}

/**
 * Open the mic and start the meter.
 *
 * config: { silenceHangMs, maxUtteranceMs }
 * events: { onClip(blob, durationMs), onSpeechStart(), onSpeechEnd(),
 *           onBargeIn(), onLevel(rms, threshold) }
 *
 * Resolves to a handle: setState, setPolicy, flush, recording, close.
 */
export async function startVoiceLoop(config, events) {
  // autoGainControl OFF on purpose: it lifts the noise floor during pauses
  // until "silence" never drops below the threshold and turns refuse to end.
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false },
  });

  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  // iOS creates contexts suspended; resume inside the launch tap's async chain.
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      /* best effort */
    }
  }
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  // Analyser only — never wired to destination, so no mic monitoring.
  ctx.createMediaStreamSource(stream).connect(analyser);
  const samples = new Float32Array(analyser.fftSize);
  const mime = pickMime();

  let policy = { ...config };
  let state = 'listen'; // 'idle' | 'listen' | 'bargein'
  let closed = false;
  let recorder = null;
  let chunks = [];
  let clipStarted = 0;
  let lastVoice = 0;
  let keepOnStop = false;
  let flushWaiters = [];
  let noiseFloor = 0.002;
  let loudSince = 0; // barge-in sustain tracking

  function rms() {
    analyser.getFloatTimeDomainData(samples);
    let sum = 0;
    for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
    return Math.sqrt(sum / samples.length);
  }

  function openClip() {
    if (recorder) return;
    let rec;
    try {
      rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    } catch {
      return; // this browser refused the container — stay silent, keep polling
    }
    chunks = [];
    clipStarted = Date.now();
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    rec.onstop = () => {
      const durationMs = Date.now() - clipStarted;
      const blob = new Blob(chunks, { type: rec.mimeType || mime || 'audio/webm' });
      chunks = [];
      const keep =
        keepOnStop && durationMs >= MIN_UTTERANCE_MS && blob.size >= MIN_CLIP_BYTES;
      if (keep) events.onClip?.(blob, durationMs);
      const waiters = flushWaiters;
      flushWaiters = [];
      waiters.forEach((w) => w());
    };
    // 1s timeslice: mobile encoders drop the final flush without it.
    rec.start(1000);
    recorder = rec;
    events.onSpeechStart?.();
  }

  function closeClip(keep) {
    const rec = recorder;
    recorder = null;
    if (!rec) {
      const waiters = flushWaiters;
      flushWaiters = [];
      waiters.forEach((w) => w());
      return;
    }
    keepOnStop = keep;
    events.onSpeechEnd?.();
    try {
      rec.requestData(); // flush the tail before stopping
    } catch {
      /* not every engine allows it */
    }
    try {
      rec.stop();
    } catch {
      /* already inactive */
    }
  }

  const timer = window.setInterval(() => {
    if (closed) return;
    const level = rms();
    const now = Date.now();
    const threshold = Math.min(MAX_RMS, Math.max(BASE_RMS, noiseFloor * FLOOR_MULT));
    events.onLevel?.(level, threshold);

    if (state === 'idle') return;

    if (state === 'bargein') {
      // Don't feed the app's own voice into the ambient floor, and demand a
      // sustained shout before believing a human is talking over it.
      if (level >= threshold * BARGE_MULT) {
        if (!loudSince) loudSince = now;
        if (now - loudSince >= BARGE_SUSTAIN_MS) {
          loudSince = 0;
          events.onBargeIn?.();
        }
      } else {
        loudSince = 0;
      }
      return;
    }

    if (level < threshold) {
      noiseFloor = noiseFloor * (1 - FLOOR_ALPHA) + level * FLOOR_ALPHA;
    } else {
      lastVoice = now;
      if (!recorder) openClip();
      return;
    }

    if (recorder) {
      const silentFor = now - lastVoice;
      const clipFor = now - clipStarted;
      if (silentFor >= policy.silenceHangMs || clipFor >= policy.maxUtteranceMs) {
        closeClip(true);
      }
    }
  }, POLL_MS);

  return {
    /** 'idle' (deaf), 'listen' (normal turn-taking), 'bargein' (interrupt watch). */
    setState(next) {
      if (state === next) return;
      state = next;
      loudSince = 0;
      // Leaving 'listen' abandons whatever was mid-capture — it belongs to a
      // turn that is over.
      if (next !== 'listen' && recorder) closeClip(false);
    },
    /** Swap turn-ending policy without dropping the mic (mode switch). */
    setPolicy(next) {
      policy = { ...policy, ...next };
    },
    /** Close the open clip now and emit it — walkie's Send button. Resolves
     *  once the clip has been handed to onClip (or immediately if none). */
    flush() {
      return new Promise((resolve) => {
        if (!recorder) {
          resolve();
          return;
        }
        flushWaiters.push(resolve);
        closeClip(true);
      });
    },
    recording: () => recorder !== null,
    close() {
      closed = true;
      window.clearInterval(timer);
      if (recorder) closeClip(false);
      stream.getTracks().forEach((t) => t.stop());
      try {
        ctx.close();
      } catch {
        /* already closed */
      }
    },
  };
}
